import { Component, EventEmitter, HostBinding, inject, Input, model, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { take } from 'rxjs';
import { BreakpointObserverService } from 'src/app/shared/services/breakpoint-observer/breakpoint-observer.service';
import { DynamicOverlayService } from 'src/app/shared/services/dynamic-overlay/dynamic-overlay.service';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { SelectListComponent, SelectListData, SelectListItem } from '../../select-list/select-list.component';

export interface FilterToolbarConfig extends SelectListItem {}

@Component({
  selector: 'app-filter-toolbar',
  template: `
    <div class="filter-toolbar-container">
      @if (showSidenavToggle && sidenavService.isSmallOrTablet()) {
        <button
          mat-button
          class=" icon-only"
          (mousedown)="$event.preventDefault()"
          (click)="sidenavService.toggle()"
          [matTooltip]="(sidenavService.opened() ? 'Global.Close' : 'Global.Open') | translate"
        >
          <mat-icon>{{ sidenavService.opened() ? 'left_panel_close' : 'left_panel_open' }}</mat-icon>
        </button>
      }

      @if (showCategoryFilter) {
        @if (!isSmallScreen()) {
          <button #resButton mat-button matSuffix (click)="openOptionsMenu(resButton)" class="flex-grow-1 custom-select-button" [class.icon-only]="isSmallScreen()">
            <span class="selected-value">{{ getSelectedLabel(options, selectedValue) | translate }}</span>
            <mat-icon class="ms-1">arrow_drop_down</mat-icon>
          </button>
        } @else {
          <button #resButton mat-button matSuffix (click)="openOptionsMenu(resButton)" class="flex-grow-1 custom-select-button" [class.icon-only]="isSmallScreen()">
            <mat-icon [class.me-1]="!isSmallScreen()">{{ getSelectedIcon(options, selectedValue) || 'filter_list' }}</mat-icon>
          </button>
        }
      }

      <mat-form-field class="filter-search" appearance="fill">
        <input matInput [ngModel]="searchTerm()" (keyup)="searchTerm.set($event.target.value)" [placeholder]="placeholder | translate" autocomplete="off" />
      </mat-form-field>
    </div>
  `,
  styleUrls: ['./filter-toolbar.component.scss'],
  standalone: true,
  imports: [FormsModule, TranslateModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatIconModule, MatButtonModule, MatTooltipModule],
})
export class FilterToolbarComponent {
  @Input() selectedValue: string;
  searchTerm = model<string>('');
  @Input() options: FilterToolbarConfig[] = [];
  @Input() placeholder = 'Filter...';
  @Input() showCategoryFilter = true;
  @Input() showSidenavToggle = false;

  public sidenavService = inject(SidenavService);

  private breakpointObserver = inject(BreakpointObserverService);
  public isSmallScreen = this.breakpointObserver.isSmallScreen;

  @HostBinding('class.no-category-filter')
  get noCategoryFilter() {
    return !this.showCategoryFilter;
  }

  @Output() selectionChange = new EventEmitter<string>();

  private overlay = inject(DynamicOverlayService);

  getSelectedLabel(options: FilterToolbarConfig[], value: any): string {
    const selected = options.find((o) => o.value === value);
    return selected ? selected.label : '';
  }

  getSelectedIcon(options: FilterToolbarConfig[], value: any): string {
    const selected = options.find((o) => o.value === value);
    return selected ? selected.icon : '';
  }

  openOptionsMenu(trigger: MatButton): void {
    const data: SelectListData = {
      title: 'Select an option',
      items: this.options.map((option) => ({ value: option.value, label: option.label })),
      selectedValue: this.selectedValue,
    };

    const { componentInstance } = this.overlay.open(trigger._elementRef, SelectListComponent, {
      hasBackdrop: true,
      data,
    });

    componentInstance.optionSelected.pipe(take(1)).subscribe((value) => {
      this.selectedValue = value;
      this.overlay.close();
      this.selectionChange.emit(this.selectedValue);
    });
  }
}
